import { useEffect, useRef, useState } from 'react';
import { getAllQuizData } from '../services/contentService';
import PremiumAccessDialog from './PremiumAccessDialog';
import {
  generateClientUUID,
  gradeQuizAttempt,
  isUserConnected,
} from '../services/progressService';
import {
  clearServerPrimaryQuizHistory,
  loadServerPrimaryDashboard,
  submitProgressAttemptWithFallback,
} from '../services/progressSyncService';
import { readJsonArray, writeJson } from '../utils/storage';
import { isMultiAnswer, hasAnswer, toggleAnswer, isSelected,
  isAnswerCorrect, answerLabels, correctAnswerLabels, attemptAnswer } from '../utils/multiAnswer';
import {
  canGradeLocally,
  questionWithServerCorrection,
} from '../utils/serverGrading';

const Quizzes = ({ onGoUpLevel, onGoBackTab, onOpenPremium }) => {
  // États pour les différents niveaux de navigation
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedLot, setSelectedLot] = useState(null);
  const [selectedSujet, setSelectedSujet] = useState(null);
  const [lockedTitle, setLockedTitle] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [quizFinished, setQuizFinished] = useState(false);
  const [correctionVisible, setCorrectionVisible] = useState(false);
  const [score, setScore] = useState(0);
  const [gradingResult, setGradingResult] = useState(null);
  const [gradingError, setGradingError] = useState(null);
  const [gradingInProgress, setGradingInProgress] = useState(false);

  // États pour les nouvelles fonctionnalités
  const [examMode, setExamMode] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [history, setHistory] = useState([]);
  const [difficultQuestions, setDifficultQuestions] = useState([]);
  const [quizStarted, setQuizStarted] = useState(false);

  // Attempt refs: stable for one started quiz, including retries of the same write.
  const timerRef = useRef(null);
  const attemptUuidRef = useRef(null);
  const startedAtRef = useRef(null);
  const finishGuardRef = useRef(false);
  const questionNavRef = useRef(null);

  // Preserve the historic icons, but take the active subjects and their titles
  // from PostgreSQL. Alias keys are deduplicated via the canonical module slug.
  const historicalSubjects = {
    culture_generale_actualite: { name: 'Culture Générale', icon: '🌍' },
    histoire_geographie_burkina: { name: 'Histoire du Burkina Faso', icon: '🏛️' },
    histoire_generale: { name: 'Histoire Générale', icon: '📜' },
    francais_litterature: { name: 'Littérature', icon: '📚' },
    mathematiques: { name: 'Mathématiques', icon: '📐' },
    physique_chimie: { name: 'Physique-Chimie', icon: '⚗️' },
    psychotechnique_logique: { name: 'Psychotechnique', icon: '🧠' },
    specialites: { name: 'Spécialité', icon: '🎯' },
    svt_sante: { name: 'SVT', icon: '🧬' },
  };


  // Mapping des données par matière (via contentService)
  const [dataMapping, setDataMapping] = useState({});
  const [loadingContent, setLoadingContent] = useState(true);
  const [contentError, setContentError] = useState(null);
  const subjects = Object.entries(dataMapping).reduce((items, [key, module]) => {
    const canonical = module?.module_slug || key;
    if (items.some((item) => item.canonical === canonical)) return items;
    const legacy = historicalSubjects[canonical] || {};
    items.push({
      key,
      canonical,
      name: module?.module_title || legacy.name || canonical.replace(/[_-]/g, ' '),
      icon: legacy.icon || '📚',
    });
    return items;
  }, []);


  const sessionActiveRef = useRef(false);
  sessionActiveRef.current = quizStarted;

  useEffect(() => {
    let isMounted = true;
    let requestVersion = 0;
    const load = () => {
      if (sessionActiveRef.current) return;
      const version = ++requestVersion;
      setContentError(null);
      setLoadingContent(true);
      getAllQuizData()
      .then((data) => {
        if (isMounted && version === requestVersion) {
          setDataMapping(data || {});
          setLoadingContent(false);
        }
      })
      .catch((err) => {
        if (isMounted && version === requestVersion) {
          setContentError(err.message || 'Erreur lors du chargement des quiz');
          setLoadingContent(false);
        }
      });
    };
    const visible = () => { if (document.visibilityState === 'visible') load(); };
    load();
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', visible);
    return () => {
      isMounted = false;
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', visible);
    };
  }, []);

  // Utilisateur connecté : le serveur est prioritaire.
  // Anonyme / panne réseau : repli sur le cache local du navigateur.
  useEffect(() => {
    let active = true;
    const currentUserId = localStorage.getItem('concours_bf_current_user');
    const isOwnedByCurrentSession = (entry) =>
      currentUserId
        ? String(entry?.user_id || '') === String(currentUserId)
        : !entry?.user_id;

    const loadLocal = () => {
      setHistory(
        readJsonArray('quiz_history').filter(isOwnedByCurrentSession)
      );
      setDifficultQuestions(
        readJsonArray('quiz_difficult').filter(isOwnedByCurrentSession)
      );
    };

    if (!currentUserId || !isUserConnected()) {
      loadLocal();
      return () => {
        active = false;
      };
    }

    loadServerPrimaryDashboard(currentUserId)
      .then((dashboard) => {
        if (!active) return;

        setHistory(
          (dashboard.activities || []).filter(
            (activity) => activity.activityType === 'quiz'
          )
        );

        const serverDifficult = (dashboard.questionStates || [])
          .filter((state) => state.is_difficult)
          .map((state) => ({
            user_id: String(currentUserId),
            question: state.question_text,
            matiere: state.module_title || state.module_slug || 'Autre',
            lot: state.domaine_slug || '',
            sujet: state.sujet_slug || '',
            failedCount: Number(state.times_wrong) || 0,
          }));

        // Historical local difficult-question entries do not always contain a
        // PostgreSQL question id, so they cannot be migrated safely. Preserve
        // them in the UI without pretending they are server-verified.
        const legacyDifficult = readJsonArray('quiz_difficult').filter(
          isOwnedByCurrentSession
        );
        const serverQuestions = new Set(
          serverDifficult.map((entry) => entry.question)
        );

        setDifficultQuestions([
          ...serverDifficult,
          ...legacyDifficult.filter(
            (entry) => !serverQuestions.has(entry.question)
          ),
        ]);
      })
      .catch(() => {
        if (active) loadLocal();
      });

    return () => {
      active = false;
    };
  }, []);

  // Effet pour gérer le timer
  useEffect(() => {
    if (examMode && timeRemaining !== null && timeRemaining > 0 && !quizFinished && quizStarted) {
      timerRef.current = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
    } else if (timeRemaining === 0 && examMode && !quizFinished && quizStarted) {
      finishQuiz();
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining, examMode, quizFinished, quizStarted]);

  // Keep the active question visible in the compact horizontal navigator.
  useEffect(() => {
    if (!quizStarted || examMode || !questionNavRef.current) return;
    const container = questionNavRef.current;
    const activeButton = container.querySelector(`[data-question-index="${currentQuestionIndex}"]`);
    if (!activeButton || typeof container.scrollTo !== 'function') return;

    const targetLeft =
      activeButton.offsetLeft - container.clientWidth / 2 + activeButton.clientWidth / 2;
    container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
  }, [currentQuestionIndex, quizStarted, examMode]);

  // Effet pour gérer le bouton Retour du header (niveaux de navigation)
  useEffect(() => {
    if (onGoUpLevel && onGoBackTab) {
      if (selectedSujet && !quizFinished) {
        // Niveau 4: Retour au choix du sujet
        onGoUpLevel(() => {
          setSelectedSujet(null);
          setCurrentQuestionIndex(0);
          setUserAnswers({});
          setQuizFinished(false);
          setCorrectionVisible(false);
          setExamMode(false);
          setTimeRemaining(null);
          setQuizStarted(false);
        }, 4);
      } else if (selectedLot && !selectedSujet) {
        // Niveau 3: Retour au choix du lot
        onGoUpLevel(() => {
          setSelectedLot(null);
          setSelectedSujet(null);
          setCurrentQuestionIndex(0);
          setUserAnswers({});
          setQuizFinished(false);
          setCorrectionVisible(false);
          setExamMode(false);
          setTimeRemaining(null);
          setQuizStarted(false);
        }, 3);
      } else if (selectedSubject && !selectedLot) {
        // Niveau 2: Retour au choix de la matière
        onGoUpLevel(() => {
          setSelectedSubject(null);
          setSelectedLot(null);
          setSelectedSujet(null);
          setCurrentQuestionIndex(0);
          setUserAnswers({});
          setQuizFinished(false);
          setCorrectionVisible(false);
          setExamMode(false);
          setTimeRemaining(null);
          setQuizStarted(false);
        }, 2);
      } else {
        // Niveau 1: Retour à l'accueil du composant
        onGoUpLevel(() => {
          setSelectedSubject(null);
          setSelectedLot(null);
          setSelectedSujet(null);
          setCurrentQuestionIndex(0);
          setUserAnswers({});
          setQuizFinished(false);
          setCorrectionVisible(false);
          setExamMode(false);
          setTimeRemaining(null);
          setQuizStarted(false);
        }, 1);
      }
    }
  }, [selectedSubject, selectedLot, selectedSujet, quizFinished, onGoUpLevel, onGoBackTab]);

  // Obtenir les domaines disponibles pour une matière (nouveau format sans lots)
  const getAvailableDomaines = (subjectKey) => {
    const data = dataMapping[subjectKey];
    if (!data) return [];
    // Nouveau format: domaines > sujets
    if (data.domaines) {
      return Object.entries(data.domaines).map(([key, domaine]) => ({
        key,
        titre: domaine.titre,
        description: domaine.description,
        sujets: domaine.sujets || []
      }));
    }
    // Ancien format: quizzes > domaines > lots > sujets (pour compatibilité)
    if (data.quizzes) {
      return Object.entries(data.quizzes).map(([key, domaine]) => {
        const allQuestions = [];
        if (domaine.lot1?.sujets) {
          domaine.lot1.sujets.forEach(sujet => {
            if (sujet.questions) {
              allQuestions.push(...sujet.questions);
            }
          });
        }
        return {
          key,
          titre: domaine.lot1?.titre || key,
          description: domaine.lot1?.description || '',
          sujets: allQuestions.length > 0 ? [{
            key: 'quiz',
            titre: 'Quiz Complet',
            nombre_questions: allQuestions.length,
            questions: allQuestions.map((q, idx) => ({ ...q, numero: idx + 1 }))
          }] : []
        };
      });
    }
    return [];
  };

  // Obtenir le domaine sélectionné (nouveau format sans lots)
  const getSelectedDomaineData = () => {
    if (!selectedSubject || !selectedLot) return null;
    const domaines = getAvailableDomaines(selectedSubject);
    return domaines.find(d => d.key === selectedLot) || null;
  };

  // Obtenir le sujet sélectionné
  const getSelectedSujetData = () => {
    const domaineData = getSelectedDomaineData();
    if (!domaineData || !selectedSujet) return null;
    return domaineData.sujets.find(s => s.key === selectedSujet) || null;
  };

  // Formater le temps
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Gérer la sélection d'une réponse
  const handleAnswerSelect = (questionNum, answerIndex) => {
    const question = getSelectedSujetData()?.questions?.find((q) => q.numero === questionNum);
    setUserAnswers((prev) => ({
      ...prev,
      [questionNum]: toggleAnswer(prev[questionNum], answerIndex, isMultiAnswer(question)),
    }));
  };

  // Passer à la question suivante
  const handleNextQuestion = () => {
    const sujetData = getSelectedSujetData();
    if (!sujetData) return;

    if (currentQuestionIndex < sujetData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      finishQuiz();
    }
  };

  // Passer à la question précédente
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Sauter à une question spécifique
  const handleJumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  // Correction autoritative : en mode API les bonnes réponses ne sont jamais
  // présentes dans le catalogue. Elles sont révélées uniquement après soumission.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const finishQuiz = async () => {
    if (finishGuardRef.current) return;

    const sujetData = getSelectedSujetData();
    const domaineData = getSelectedDomaineData();
    if (!sujetData || !domaineData || sujetData.questions.length === 0) return;

    finishGuardRef.current = true;
    setGradingError(null);
    setGradingInProgress(true);

    const completedAt = new Date();
    const startedAt = startedAtRef.current || completedAt.toISOString();
    const durationSeconds = Math.max(
      0,
      Math.round((completedAt.getTime() - new Date(startedAt).getTime()) / 1000)
    );
    const currentUserId = localStorage.getItem('concours_bf_current_user');
    const moduleSlug = dataMapping[selectedSubject]?.module_slug || selectedSubject;
    const answers = sujetData.questions.map((q) =>
      attemptAnswer(q, userAnswers[q.numero])
    );
    const payload = {
      client_uuid: attemptUuidRef.current,
      module_slug: moduleSlug || 'general',
      domaine_slug: selectedLot || 'general',
      sujet_slug: selectedSujet || 'general',
      sujet_titre: sujetData.titre || 'Quiz',
      matiere_titre:
        subjects.find((subject) => subject.key === selectedSubject)?.name ||
        selectedSubject,
      domaine_titre: domaineData.titre || selectedLot,
      exam_mode: examMode,
      started_at: startedAt,
      completed_at: completedAt.toISOString(),
      duration_seconds: durationSeconds,
      answers,
    };

    try {
      let grading = null;
      const localGradeAvailable = canGradeLocally(sujetData.questions);

      // JSON development fixtures may still contain embedded answer keys.
      // Production API payloads never do, so production always falls through
      // to the authoritative server grading path below.
      if (localGradeAvailable) {
        const correctedCount = sujetData.questions.reduce(
          (count, q) =>
            count + (isAnswerCorrect(q, userAnswers[q.numero]) ? 1 : 0),
          0
        );
        grading = {
          score:
            Math.round(
              (correctedCount / sujetData.questions.length) * 20 * 10
            ) / 10,
          correctCount: correctedCount,
          totalQuestions: sujetData.questions.length,
          correction: [],
        };
      }

      const hasDatabaseIds = answers.every(
        (answer) => Number.isInteger(answer.question_id) && answer.question_id > 0
      );

      if (hasDatabaseIds && attemptUuidRef.current) {
        if (isUserConnected()) {
          const submitted = await submitProgressAttemptWithFallback(
            currentUserId,
            'quiz',
            payload
          );
          grading = submitted?.result?.grading || grading;

          if (submitted?.queued && !grading) {
            throw new Error(
              'La tentative a été conservée localement, mais une connexion est nécessaire pour obtenir le score et la correction.'
            );
          }
        } else if (!grading) {
          const response = await gradeQuizAttempt(payload);
          grading = response?.grading || null;
        }
      }

      if (!grading) {
        throw new Error(
          'Impossible de vérifier les réponses pour le moment. Vérifiez votre connexion puis réessayez.'
        );
      }

      const correctedQuestions = sujetData.questions.map((q) =>
        questionWithServerCorrection(q, grading)
      );
      const wrongAnswers = correctedQuestions.filter(
        (q) => !isAnswerCorrect(q, userAnswers[q.numero])
      );
      const finalScore = Number(grading.score);
      const correctCount = Number(grading.correctCount);

      setGradingResult(grading);
      setScore(Number.isFinite(finalScore) ? finalScore : 0);
      setQuizFinished(true);
      setCorrectionVisible(false);

      const newHistoryEntry = {
        user_id: currentUserId || null,
        date: completedAt.toISOString(),
        client_uuid: attemptUuidRef.current,
        sujet: sujetData.titre,
        lot: domaineData.titre,
        matiere:
          subjects.find((s) => s.key === selectedSubject)?.name ||
          selectedSubject,
        score: Number.isFinite(finalScore) ? finalScore : 0,
        totalQuestions:
          Number(grading.totalQuestions) || sujetData.questions.length,
        correctAnswers: Number.isFinite(correctCount) ? correctCount : 0,
        examMode: examMode,
      };
      const newHistory = [newHistoryEntry, ...history];
      setHistory(newHistory);

      const otherUsersHistory = readJsonArray('quiz_history').filter((entry) =>
        currentUserId
          ? String(entry?.user_id || '') !== String(currentUserId)
          : Boolean(entry?.user_id)
      );
      writeJson('quiz_history', [...newHistory, ...otherUsersHistory]);

      if (wrongAnswers.length > 0) {
        const newDifficult = [...difficultQuestions];
        wrongAnswers.forEach((q) => {
          const exists = newDifficult.find((dq) => dq.question === q.question);
          if (!exists) {
            newDifficult.push({
              user_id: currentUserId || null,
              question: q.question,
              matiere: subjects.find((s) => s.key === selectedSubject)?.name,
              lot: domaineData.titre,
              sujet: sujetData.titre,
              failedCount: 1,
            });
          } else {
            exists.failedCount += 1;
          }
        });
        newDifficult.sort((a, b) => b.failedCount - a.failedCount);
        setDifficultQuestions(newDifficult);

        const otherUsersDifficult = readJsonArray('quiz_difficult').filter(
          (entry) =>
            currentUserId
              ? String(entry?.user_id || '') !== String(currentUserId)
              : Boolean(entry?.user_id)
        );
        writeJson('quiz_difficult', [
          ...newDifficult,
          ...otherUsersDifficult,
        ]);
      }
    } catch (error) {
      setGradingError(
        error?.message ||
          'Impossible de vérifier les réponses. Vérifiez votre connexion.'
      );
      finishGuardRef.current = false;
    } finally {
      setGradingInProgress(false);
    }
  };

  // Refaire le quiz
  const handleRestartQuiz = () => {
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setQuizFinished(false);
    setCorrectionVisible(false);
    setScore(0);
    setGradingResult(null);
    setGradingError(null);
    setGradingInProgress(false);
    setExamMode(false);
    setTimeRemaining(null);
    setQuizStarted(false);
    attemptUuidRef.current = null;
    startedAtRef.current = null;
    finishGuardRef.current = false;
  };

  // Démarrer le quiz
  const handleStartQuiz = (isExamMode) => {
    attemptUuidRef.current = generateClientUUID();
    startedAtRef.current = new Date().toISOString();
    finishGuardRef.current = false;
    setGradingResult(null);
    setGradingError(null);
    setGradingInProgress(false);

    setExamMode(isExamMode);
    setQuizStarted(true);
    const sujetData = getSelectedSujetData();
    if (sujetData) {
      if (isExamMode) {
        // 1 minute par question
        setTimeRemaining(sujetData.questions.length * 60);
      } else {
        setTimeRemaining(null);
      }
    }
  };

  // Supprimer l'historique visible sans muter les tentatives serveur.
  const handleClearHistory = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer tout l\'historique ?')) {
      return;
    }

    const currentUserId = localStorage.getItem('concours_bf_current_user');
    setHistory([]);

    try {
      await clearServerPrimaryQuizHistory(currentUserId);
    } catch (error) {
      console.warn(
        'Effacement serveur différé; l’historique local reste masqué pour cette session.',
        error
      );
    }
  };

  // Données courantes
  const domaineData = getSelectedDomaineData();
  const sujetData = getSelectedSujetData();
  const quizActive = Boolean(
    selectedSubject && selectedLot && selectedSujet && domaineData && sujetData && !quizFinished && quizStarted
  );
  const answeredCount = sujetData?.questions?.reduce(
    (count, question) => count + (hasAnswer(userAnswers[question.numero]) ? 1 : 0),
    0
  ) || 0;
  const answeredProgress = sujetData?.questions?.length
    ? (answeredCount / sujetData.questions.length) * 100
    : 0;

  return (
    <div className={quizActive ? 'exam-session cb-quizzes-page' : 'space-y-8 cb-quizzes-page'}>
      {/* Header Section */}
      {!quizActive && (
      <section className="cb-section-intro">
        <span className="cb-section-kicker">ENTRAÎNEMENT</span>
        <h2>Quiz et QCM</h2>
        <p>Choisissez une matière, testez vos connaissances et suivez votre progression.</p>
      </section>
      )}

      {loadingContent && (
        <div className="flex items-center justify-center p-12 text-secondary">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          Chargement des quiz...
        </div>
      )}

      {contentError && (
        <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-2xl">
          ⚠️ {contentError}
        </div>
      )}

      {gradingError && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 rounded-2xl border border-amber-200 dark:border-amber-800">
          {gradingError}
        </div>
      )}

      {lockedTitle && <PremiumAccessDialog contentTitle={lockedTitle}
        onClose={() => setLockedTitle(null)}
        onUpgrade={() => { setLockedTitle(null); onOpenPremium?.(); }} />}

      {/* NIVEAU 0: Historique et Questions difficiles */}
      {!loadingContent && !contentError && !selectedSubject && (
        <>
          <section className="cb-catalog-heading" aria-labelledby="cb-quiz-catalog-title">
            <p className="cb-eyebrow">MATIÈRES</p>
            <h1 id="cb-quiz-catalog-title">Choisissez une matière</h1>
            <p>Accédez ensuite aux domaines et aux sujets disponibles pour vous entraîner.</p>
          </section>

          {/* Historique des scores */}
          {history.length > 0 && (
            <div className="surface-card rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  📊 Historique des scores
                </h2>
                <button
                  onClick={handleClearHistory}
                  className="text-sm text-red-500 hover:text-red-600"
                >
                  Effacer tout
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-2 text-sm text-secondary">Date</th>
                      <th className="pb-2 text-sm text-secondary">Matière</th>
                      <th className="pb-2 text-sm text-secondary">Score</th>
                      <th className="pb-2 text-sm text-secondary">Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice(0, 5).map((entry, index) => (
                      <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="py-2 text-sm">
                          {new Date(entry.date).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="py-2 text-sm">{entry.matiere}</td>
                        <td className="py-2 text-sm font-bold text-blue-600">
                          {entry.score}/20
                        </td>
                        <td className="py-2 text-sm">
                          {entry.examMode ? '⏱️ Examen' : '📚 Entraînement'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {history.length > 5 && (
                <p className="text-sm text-secondary mt-2 text-center">
                  {history.length} tentatives au total
                </p>
              )}
            </div>
          )}

          {/* Questions difficiles */}
          {difficultQuestions.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-red-800 dark:text-red-400 mb-4">
                ⚠️ Questions à réviser ({difficultQuestions.length})
              </h2>
              <div className="space-y-2">
                {difficultQuestions.slice(0, 3).map((q, index) => (
                  <div
                    key={index}
                    className="p-3 surface-card rounded-lg border border-red-100 dark:border-red-800"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-primary line-clamp-2">
                        {q.question}
                      </p>
                      <span className="text-xs bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 px-2 py-1 rounded-full ml-2">
                        {q.failedCount}× échoué
                      </span>
                    </div>
                    <p className="text-xs text-secondary mt-1">{q.matiere}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 cb-quiz-subject-grid">
            {subjects.map((sub) => {
              const domaines = getAvailableDomaines(sub.key);
              return (
                <button
                  key={sub.key}
                  onClick={() => setSelectedSubject(sub.key)}
                  className="p-8 rounded-2xl border transition-all text-left flex flex-col items-center group surface-card border-color hover:border-blue-300 hover:shadow-md cb-quiz-subject-card"
                >
                  <span className="text-5xl mb-4 group-hover:scale-110 transition-transform">
                    {sub.icon}
                  </span>
                  <h3 className="text-xl font-bold mb-1 text-center text-gray-900 dark:text-white">{sub.name}</h3>
                  <p className="text-sm text-secondary">
                    {domaines.length} domaine{domaines.length !== 1 ? 's' : ''}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="cb-catalog-hint">
            Sélectionnez une matière pour accéder aux quiz.
          </div>
        </>
      )}

      {/* NIVEAU 1: Liste des lots */}
      {selectedSubject && !selectedLot && (
        <>
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-blue-800 dark:text-blue-400">
              {subjects.find((s) => s.key === selectedSubject)?.name}
            </h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 cb-quiz-domain-grid">
            {getAvailableDomaines(selectedSubject).map((domaine) => (
              <button
                key={domaine.key}
                onClick={() => setSelectedLot(domaine.key)}
                className="p-6 rounded-2xl border transition-all text-left surface-card border-color hover:border-blue-300 hover:shadow-md cb-quiz-domain-card"
              >
                <h3 className="text-xl font-bold mb-2 text-blue-700 dark:text-blue-400">
                  {domaine.titre}
                </h3>
                <p className="text-sm text-secondary mb-4">
                  {domaine.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium">
                    {domaine.sujets ? domaine.sujets.length : 0} sujet{domaine.sujets?.length !== 1 ? 's' : ''}
                  </span>
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    Choisir
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* NIVEAU 2: Liste des sujets dans un lot */}
      {selectedSubject && selectedLot && !selectedSujet && domaineData && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-blue-800 dark:text-blue-400">
                {domaineData.titre}
              </h1>
              <p className="text-secondary mt-1">{domaineData.description}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domaineData.sujets && domaineData.sujets.map((sujet) => (
              <button
                key={sujet.key}
                onClick={() => {
                  if (sujet.locked) setLockedTitle(sujet.titre || 'Ce Quiz');
                  else setSelectedSujet(sujet.key);
                }}
                className="p-6 rounded-2xl border transition-all text-left surface-card border-color hover:border-purple-400 hover:shadow-md"
              >
                <h3 className="text-xl font-bold mb-2 text-blue-700 dark:text-blue-400">
                  {sujet.titre}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                    {sujet.nombre_questions ?? sujet.questions?.length ?? 0} questions
                  </span>
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    {sujet.locked ? '🔒 Voir Premium' : 'Commencer'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* NIVEAU 3: Choix du mode avant le quiz */}
      {selectedSubject && selectedLot && selectedSujet && domaineData && sujetData && !quizFinished && !quizStarted && (
        <div className="surface-card rounded-2xl p-8 shadow-lg cb-mode-picker">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            {domaineData.titre} - {sujetData.titre}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Mode entraînement */}
            <button
              onClick={() => handleStartQuiz(false)}
              className="p-8 rounded-2xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:border-green-500 transition-all text-center"
            >
              <div className="text-5xl mb-4">📚</div>
              <h3 className="text-xl font-bold text-green-700 dark:text-green-400 mb-2">
                Mode entraînement
              </h3>
              <ul className="text-sm text-secondary text-left space-y-1">
                <li>✅ Pas de limite de temps</li>
                <li>✅ Peut revenir sur les questions</li>
                <li>✅ Idéal pour apprendre</li>
              </ul>
            </button>

            {/* Mode examen */}
            <button
              onClick={() => handleStartQuiz(true)}
              className="p-8 rounded-2xl border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:border-red-500 transition-all text-center"
            >
              <div className="text-5xl mb-4">⏱️</div>
              <h3 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">
                Mode examen
              </h3>
              <ul className="text-sm text-secondary text-left space-y-1">
                <li>⏱️ {sujetData.questions.length} minute(s)</li>
                <li>❌ Pas de retour en arrière</li>
                <li>🎯 Conditions réelles</li>
              </ul>
            </button>
          </div>
        </div>
      )}

      {/* NIVEAU 4: Le quiz interactif */}
      {selectedSubject && selectedLot && selectedSujet && domaineData && sujetData && !quizFinished && quizStarted && (
        <>
          {/* Barre de progression avec timer */}
          <div className="exam-status-panel surface-card rounded-xl p-3 sm:p-4 shadow-sm border border-color">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h2 className="text-base sm:text-lg font-bold text-blue-800 dark:text-blue-400 leading-tight">
                  {domaineData.titre} - {sujetData.titre}
                </h2>
                {examMode && (
                  <p className="text-xs text-red-500 mt-0.5">
                    ⏱️ Mode examen - Pas de retour en arrière
                  </p>
                )}
              </div>
              {examMode && timeRemaining !== null && (
                <div className={`text-lg sm:text-xl whitespace-nowrap font-bold ${timeRemaining < 60 ? 'text-red-500' : 'text-primary'}`}>
                  ⏱️ {formatTime(timeRemaining)}
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div
              className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-1"
              role="progressbar"
              aria-label="Progression des questions traitées"
              aria-valuemin="0"
              aria-valuemax={sujetData.questions.length}
              aria-valuenow={answeredCount}
            >
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${answeredProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-between gap-3 text-xs sm:text-sm text-secondary">
              <span>{answeredCount} / {sujetData.questions.length} traitée{answeredCount !== 1 ? 's' : ''}</span>
              <span>Question {currentQuestionIndex + 1} / {sujetData.questions.length}</span>
            </div>

            {/* Navigation entre les questions (pas en mode examen) */}
            {!examMode && (
              <div
                ref={questionNavRef}
                className="flex flex-nowrap gap-1.5 mt-2 overflow-x-auto pb-1"
                aria-label="Navigation entre les questions"
              >
                {sujetData.questions.map((q, index) => (
                  <button
                    key={q.numero}
                    onClick={() => handleJumpToQuestion(index)}
                    data-question-index={index}
                    className={`w-7 h-7 shrink-0 rounded-full text-xs font-bold transition-all ${
                      currentQuestionIndex === index
                        ? 'bg-blue-600 text-white'
                        : hasAnswer(userAnswers[q.numero])
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-secondary'
                    }`}
                  >
                    {q.numero}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Question */}
          <div className="exam-question-panel surface-card rounded-xl p-4 sm:p-5 shadow-sm border border-color">
            <div className="mb-3">
              <span className="inline-block px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium mb-2">
                Question {sujetData.questions[currentQuestionIndex].numero}
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-snug">
                {sujetData.questions[currentQuestionIndex].question}
              </h3>
              {isMultiAnswer(sujetData.questions[currentQuestionIndex]) && (
                <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  Plusieurs réponses sont correctes : cochez toutes les bonnes propositions.
                </p>
              )}
            </div>

            <div className="space-y-2 sm:space-y-3">
              {sujetData.questions[currentQuestionIndex].options.map((option, index) => (
                <button
                  key={index}
                  onClick={() =>
                    handleAnswerSelect(sujetData.questions[currentQuestionIndex].numero, index)
                  }
                  aria-label={`Réponse ${String.fromCharCode(65 + index)} : ${option}`}
                  aria-pressed={isSelected(userAnswers[sujetData.questions[currentQuestionIndex].numero], index)}
                  className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border-2 text-left transition-all min-h-11 ${
                    isSelected(userAnswers[sujetData.questions[currentQuestionIndex].numero], index)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                  }`}
                >
                  <span className="inline-flex w-7 h-7 shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-primary font-bold mr-3">
                    {isMultiAnswer(sujetData.questions[currentQuestionIndex])
                      ? (isSelected(userAnswers[sujetData.questions[currentQuestionIndex].numero], index) ? '☑' : '☐')
                      : String.fromCharCode(65 + index)}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Boutons de navigation */}
          <div className="exam-actions flex justify-between gap-3">
            <button
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0 || examMode}
              className={`px-4 sm:px-5 py-2.5 rounded-lg font-bold transition-all min-h-11 ${
                currentQuestionIndex === 0 || examMode
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Précédent
            </button>

            <button
              onClick={handleNextQuestion}
              disabled={
                gradingInProgress ||
                !hasAnswer(userAnswers[sujetData.questions[currentQuestionIndex].numero])
              }
              className={`px-4 sm:px-6 py-2.5 rounded-lg font-bold transition-all min-h-11 ${
                gradingInProgress ||
                !hasAnswer(userAnswers[sujetData.questions[currentQuestionIndex].numero])
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {gradingInProgress
                ? 'Correction…'
                : currentQuestionIndex < sujetData.questions.length - 1
                ? 'Suivant'
                : 'Terminer'}
            </button>
          </div>
        </>
      )}

      {/* NIVEAU 5: Résultats du quiz */}
      {quizFinished && domaineData && sujetData && (
        <div className="surface-card rounded-2xl p-8 shadow-lg cb-results-panel">
          {/* Score affiché */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Quiz terminé !
            </h2>
            <div className="inline-flex flex-col items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-xl mb-4">
              <span className="text-4xl font-extrabold">{score}</span>
              <span className="text-sm">/ 20</span>
            </div>
            <p className="text-xl text-secondary">
              {score >= 16 ? 'Excellent !' : score >= 12 ? 'Très bien !' : score >= 8 ? 'Bien !' : 'Continuez à pratiquer !'}
            </p>
            {examMode && (
              <p className="text-sm text-secondary mt-2">Mode examen</p>
            )}
          </div>

          {/* Menu après le quiz */}
          {!correctionVisible ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleRestartQuiz}
                className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white text-lg font-bold rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                🔄 Refaire le sujet
              </button>
              <button
                onClick={() => setCorrectionVisible(true)}
                className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white text-lg font-bold rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                👁️ Voir la correction
              </button>
            </div>
          ) : (
            <>
              {/* Correction affichée */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Détail de vos réponses
                  </h3>
                  <button
                    onClick={() => setCorrectionVisible(false)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Masquer la correction
                  </button>
                </div>
                {sujetData.questions.map((originalQuestion) => {
                  const q = questionWithServerCorrection(
                    originalQuestion,
                    gradingResult
                  );
                  const isCorrect = isAnswerCorrect(
                    q,
                    userAnswers[q.numero]
                  );
                  return (
                    <div
                      key={q.numero}
                      className={`p-4 rounded-xl border ${
                        isCorrect
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <span className={`text-2xl ${isCorrect ? '✅' : '❌'}`}>
                          {isCorrect ? 'Correct' : 'Incorrect'}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {q.numero}. {q.question}
                          </p>
                          <p className="text-sm mt-1">
                            <span className="text-secondary">Votre réponse: </span>
                            <span className={isCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                              {answerLabels(q, userAnswers[q.numero])}
                            </span>
                          </p>
                          {!isCorrect && (
                            <p className="text-sm mt-1">
                              <span className="text-green-600 dark:text-green-400">Bonne réponse: </span>
                              {correctAnswerLabels(q)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Boutons après avoir vu la correction */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleRestartQuiz}
                  className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white text-lg font-bold rounded-xl transition-all shadow-lg hover:shadow-xl"
                >
                  🔄 Refaire le sujet
                </button>
                <button
                  onClick={() => {
                    setSelectedSubject(null);
                    setSelectedLot(null);
                    setSelectedSujet(null);
                    setCurrentQuestionIndex(0);
                    setUserAnswers({});
                    setQuizFinished(false);
                    setCorrectionVisible(false);
                    setExamMode(false);
                    setTimeRemaining(null);
                    setQuizStarted(false);
                  }}
                  className="px-8 py-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-lg font-bold rounded-xl transition-all text-gray-700 dark:text-gray-200"
                >
                  🏠 Retour à l'accueil
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Quizzes;
